import { useState, useRef, useEffect } from "react";
import { useAuth } from "../auth";
import { emitAttendanceRefresh } from "../attendanceEvents";
import api from "../api";

export default function CheckIn() {
  const { me } = useAuth();
  const [action,   setAction]   = useState("checkin");
  const [msg,      setMsg]      = useState("");
  const [msgOk,    setMsgOk]    = useState(null);
  const [busy,     setBusy]     = useState(false);
  const [geoNote,  setGeoNote]  = useState("Fetching location...");
  const [todayAtt, setTodayAtt] = useState(null);
  const videoRef  = useRef(null);
  const streamRef = useRef(null);
  const geoRef    = useRef(null);

  const stopCam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  useEffect(() => {
    let mounted = true;
    let watchId = null;
    let geoTimer = null;

    // Camera with isMounted guard
    navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
      audio: false
    }).then(stream => {
      if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    }).catch(() => {
      if (mounted) { setMsg("Camera unavailable."); setMsgOk(false); }
    });

    // watchPosition for best accuracy
    if (navigator.geolocation) {
      let settled = false;
      const onPos = (p) => {
        if (!mounted) return;
        const acc = Math.round(p.coords.accuracy);
        geoRef.current = { latitude: p.coords.latitude, longitude: p.coords.longitude };
        setGeoNote("Location captured (\u00b1" + acc + " m) \u2014 sent with attendance.");
        if (acc <= 200 && !settled) {
          settled = true;
          if (watchId !== null) navigator.geolocation.clearWatch(watchId);
        }
      };
      const onErr = () => {
        if (mounted) {
          geoRef.current = null;
          setGeoNote("Location unavailable.");
        }
      };
      watchId = navigator.geolocation.watchPosition(onPos, onErr, {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0,
      });
      geoTimer = setTimeout(() => {
        if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      }, 25000);
    }

    loadTodayAtt();

    return () => {
      mounted = false;
      if (watchId !== null) navigator.geolocation.clearWatch(watchId);
      if (geoTimer) clearTimeout(geoTimer);
      stopCam();
    };
  }, []);

  const loadTodayAtt = () => {
    const now = new Date();
    const month = now.getFullYear() + "-" + String(now.getMonth()+1).padStart(2,"0");
    api.get("/portal/attendance?month=" + month).then(d => {
      const today = now.toISOString().slice(0,10);
      if (d.days?.[today]) setTodayAtt(d.days[today]);
    }).catch(()=>{});
  };

  const captureFrames = async () => {
    const video = videoRef.current;
    if (!video?.videoWidth) return [];
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth; canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    const frames = [];
    for (let i = 0; i < 4; i++) {
      ctx.drawImage(video, 0, 0);
      frames.push(canvas.toDataURL("image/jpeg", 0.85));
      if (i < 3) await new Promise(r => setTimeout(r, 280));
    }
    return frames;
  };

  const doCheckin = async () => {
    if (busy) return;
    if (!videoRef.current?.videoWidth) { setMsg("Camera not ready."); setMsgOk(false); return; }
    if (!me?.face_enrolled) { setMsg("No face enrolled."); setMsgOk(false); return; }
    setBusy(true); setMsg("Hold still..."); setMsgOk(null);
    try {
      const images_b64 = await captureFrames();
      const d = await api.post("/portal/checkin", { images_b64, action, ...(geoRef.current || {}) });
      setMsg(d.message); setMsgOk(d.verified);
      if (d.verified) setTimeout(() => { loadTodayAtt(); emitAttendanceRefresh(); setMsg(""); }, 1500);
    } catch(e) { setMsg(e.message); setMsgOk(false); }
    finally { setBusy(false); }
  };

  const color = action === "checkin" ? "var(--accent)" : "#D97706";
  const statusColors = { present:"green", late:"orange", half_day:"orange", absent:"red" };

  return (
    <div style={{maxWidth:520}}>
      <h2 className="page-title">Check In / Out</h2>
      <p className="page-sub">Your face is verified against your profile before attendance is recorded.</p>

      {todayAtt && (
        <div className="card" style={{marginTop:14,marginBottom:16,display:"flex",gap:28,flexWrap:"wrap"}}>
          {[["First In",todayAtt.first_in],["Last Out",todayAtt.last_out],
            ["Hours",todayAtt.total_hours!=="-"?todayAtt.total_hours+"h":"--"]].map(([k,v])=>(
            <div key={k}><div style={{fontSize:11,color:"var(--muted)",marginBottom:2}}>{k}</div><b style={{fontSize:15}}>{v}</b></div>
          ))}
          <div>
            <div style={{fontSize:11,color:"var(--muted)",marginBottom:2}}>Status</div>
            <span className={"pill " + (statusColors[todayAtt.status]||"gray")}>{todayAtt.status}</span>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:0,marginTop:16,marginBottom:18,
        border:"1px solid var(--line)",borderRadius:10,overflow:"hidden",width:"fit-content"}}>
        {[["checkin","Check In","var(--accent)"],["checkout","Check Out","#D97706"]].map(([val,label,col])=>(
          <button key={val} onClick={()=>{setAction(val);setMsg("");}}
            style={{padding:"10px 28px",border:"none",cursor:"pointer",fontWeight:600,fontSize:14,
              background:action===val?col:"var(--panel)",color:action===val?"#fff":"var(--muted)"}}>
            {label}
          </button>
        ))}
      </div>

      <div style={{width:260,height:260,borderRadius:"50%",padding:6,marginBottom:20,
        border:busy?"none":"6px solid "+color,
        background:busy?"conic-gradient("+color+" 0 18%,var(--line) 18% 100%)":"transparent",
        animation:busy?"spin 1.1s linear infinite":"none"}}>
        <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
        <div style={{width:"100%",height:"100%",borderRadius:"50%",overflow:"hidden",background:"#0B1220"}}>
          <video ref={videoRef} autoPlay playsInline muted
            style={{width:"100%",height:"100%",objectFit:"cover",transform:"scaleX(-1)"}}/>
        </div>
      </div>

      <button onClick={doCheckin} disabled={busy}
        style={{display:"flex",alignItems:"center",gap:8,background:color,color:"#fff",border:"none",
          borderRadius:999,padding:"13px 32px",fontSize:15,fontWeight:700,
          cursor:busy?"not-allowed":"pointer",opacity:busy?0.7:1,marginBottom:14}}>
        <span style={{fontSize:18}}>&#9673;</span>{busy?"Verifying...":"Capture & Verify"}
      </button>

      {msg && (
        <div style={{fontSize:14,fontWeight:500,padding:"10px 14px",borderRadius:8,marginBottom:10,
          background:msgOk===true?"#DCFCE7":msgOk===false?"#FEE2E2":"var(--soft)",
          color:msgOk===true?"#166534":msgOk===false?"#991B1B":"var(--muted)"}}>
          {msg}
        </div>
      )}
      <div style={{fontSize:12,color:"var(--muted)"}}>{geoNote}</div>
    </div>
  );
}
